import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { OFFICE_MESSAGE_TEMPLATES, RULES, cleanPhrases, newMessageId, type ChatThread, type DriverStatusRow, type MessageMark } from '@torapon/shared';
import { Dialog, useToast } from '../components/ui';
import { api, HttpError } from '../lib/api';
import { broadcast, saveOfficePhrases, sendToDriver, useOfficePhrases, watchThread, type ChatLine } from '../lib/chat';
import { useDirectory, useNames } from '../lib/directory';
import { useMe } from '../lib/me';
import { useLive } from '../lib/realtime';
import { useSettings } from '../lib/settings';

const hm = (d: Date | null) => (d ? `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}` : '送信中…');

/** チャット（仕様書「事務所とのチャット」）：左にドライバー（未読を上に）、右にやり取り。事務所はスタッフ全員で 1 つの窓口 */
export function ChatPage() {
  const me = useMe();
  const dir = useDirectory();
  const names = useNames();
  const [sp, setSp] = useSearchParams();
  const driverId = sp.get('driver') ?? '';
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const loadThreads = () => api.get<{ threads: ChatThread[] }>('/v1/office/chats').then((r) => setThreads(r.threads)).catch(() => {});
  useEffect(() => { void loadThreads(); }, []);
  useLive(['messages'], loadThreads);
  const list = useMemo(() => {
    const t = new Map(threads.map((x) => [x.driverId, x]));
    const ids = new Set([...[...dir.drivers.entries()].filter(([, d]) => d.active).map(([id]) => id), ...threads.map((x) => x.driverId)]);
    return [...ids].map((id) => ({ id, name: names.driver(id), unread: t.get(id)?.unread ?? 0, lastAt: t.get(id)?.lastAt ?? '' }))
      .sort((a, b) => b.unread - a.unread || b.lastAt.localeCompare(a.lastAt) || a.name.localeCompare(b.name, 'ja'));
  }, [threads, dir, names]);
  const [casting, setCasting] = useState(false);
  const { settings, loaded } = useSettings();
  if (loaded && !settings.features.chat) return <div className="page"><p className="muted">チャットは事務所の設定で止めてあります（「システム」の設定で ON にできます）。</p></div>;
  return (
    <div className="page" style={{ display: 'grid', gridTemplateColumns: '260px minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
      {casting && me && <BroadcastDialog me={me} onClose={() => setCasting(false)} />}
      <div style={{ display: 'grid', gap: 8 }}>
      <button className="btn" onClick={() => setCasting(true)}>一斉連絡を送る</button>
      <nav className="panel" aria-label="ドライバー" style={{ maxHeight: 'calc(100vh - 170px)', overflow: 'auto' }}>
        {list.length === 0 ? <p className="muted" style={{ padding: 16 }}>ドライバーがいません</p> : list.map((x) => (
          <button key={x.id} onClick={() => setSp({ driver: x.id })} aria-current={x.id === driverId ? 'true' : undefined}
            style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', border: 0, borderBottom: '1px solid var(--line)', background: x.id === driverId ? 'var(--surface-2, #f1f1f1)' : 'transparent', cursor: 'pointer', textAlign: 'left', font: 'inherit' }}>
            <span><b>{x.name}</b>{x.lastAt && <small className="muted" style={{ display: 'block' }}>{hm(new Date(x.lastAt))}</small>}</span>
            {x.unread > 0 && <em className="badge" style={{ position: 'static' }} aria-label={`未読 ${x.unread} 件`}>{x.unread}</em>}
          </button>
        ))}
      </nav>
      </div>
      {me && driverId ? <Thread key={driverId} me={me} driverId={driverId} onRead={loadThreads} /> : <section className="panel" style={{ padding: 24 }}><p className="muted" style={{ margin: 0 }}>左からドライバーを選んでください</p></section>}
    </div>
  );
}

function Thread({ me, driverId, onRead }: { me: { id: string; officeId: string }; driverId: string; onRead: () => void }) {
  const names = useNames();
  const [lines, setLines] = useState<ChatLine[] | null>(null);
  const [marks, setMarks] = useState<Map<string, MessageMark>>(new Map());
  const [text, setText] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [no, setNo] = useState('');
  const [err, setErr] = useState('');
  const phrases = useOfficePhrases(me.officeId);
  const [editing, setEditing] = useState(false);
  // 送ったばかりで、まだ印（既読など）が付いていないメッセージ
  const [sending, setSending] = useState<Set<string>>(new Set());
  const bottom = useRef<HTMLDivElement>(null);
  useEffect(() => watchThread(me.officeId, driverId, setLines, () => setErr('やり取りを読めませんでした')), [me.officeId, driverId]);
  const loadMarks = () => api.get<{ marks: MessageMark[] }>(`/v1/office/messages?driverId=${encodeURIComponent(driverId)}`).then((r) => setMarks(new Map(r.marks.map((m) => [m.id, m])))).catch(() => {});
  useEffect(() => { void loadMarks(); }, [driverId]);
  useLive(['messages'], loadMarks);
  // 開いている間に届いたドライバーのメッセージは既読にする
  const unread = (lines ?? []).filter((l) => l.from === 'driver' && !marks.get(l.id)?.readAt && marks.has(l.id)).length;
  useEffect(() => {
    if (unread > 0 && document.visibilityState === 'visible') void api.post('/v1/office/messages/read', { driverId }).then(() => { void loadMarks(); onRead(); }).catch(() => {});
  }, [unread, driverId]);
  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [lines?.length]);
  // 返事を待たずに入力欄を空にする（一覧には Firestore が先に出す）。送れなければ入力を戻して知らせる
  const post = (body: string, o: { urgent: boolean; deliveryNo: string | null; kind: 'text' | 'template' }, restore?: () => void) => {
    const id = newMessageId();
    setSending((x) => new Set(x).add(id));
    sendToDriver(me, driverId, body, { ...o, id }).then(() => loadMarks())
      .catch((er) => { setErr(er instanceof HttpError ? er.message : '送れませんでした'); restore?.(); })
      .finally(() => setSending((x) => { const n = new Set(x); n.delete(id); return n; }));
  };
  const send = (e: FormEvent) => {
    e.preventDefault(); setErr('');
    const body = text.trim();
    const dno = no.trim().toUpperCase();
    if (!body) return;
    if (body.length > RULES.chatTextMax) { setErr(`${RULES.chatTextMax} 文字までです`); return; }
    if (dno && !/^[0-9A-Z]{6}$/.test(dno)) { setErr('配送番号は英数字 6 文字です'); return; }
    const was = { text, no, urgent };
    setText(''); setNo(''); setUrgent(false);
    post(body, { urgent: was.urgent, deliveryNo: dno || null, kind: 'text' }, () => { setText((c) => c || was.text); setNo((c) => c || was.no); setUrgent(was.urgent); });
  };
  // 定型文は押したらそのまま送る（至急・配送番号の欄はそのまま使い、送ったら空にする）
  const sendPhrase = (p: string) => {
    setErr('');
    const dno = no.trim().toUpperCase();
    if (dno && !/^[0-9A-Z]{6}$/.test(dno)) { setErr('配送番号は英数字 6 文字です'); return; }
    const was = { no, urgent };
    setNo(''); setUrgent(false);
    post(p, { urgent: was.urgent, deliveryNo: dno || null, kind: 'template' }, () => { setNo((c) => c || was.no); setUrgent(was.urgent); });
  };
  return (
    <section className="panel" aria-label={`${names.driver(driverId)}さんとのチャット`} style={{ display: 'grid', gridTemplateRows: 'auto minmax(0,1fr) auto', height: 'calc(100vh - 120px)' }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between' }}>
        <b>{names.driver(driverId)}</b><Link to={`/drivers/${driverId}`}>ドライバー詳細</Link>
      </div>
      <div style={{ overflow: 'auto', padding: 16, display: 'grid', gap: 10, alignContent: 'start' }} aria-live="polite">
        {lines === null ? <p className="muted">読み込み中…</p> : lines.length === 0 ? <p className="muted">まだやり取りがありません</p> : lines.map((l) => {
          const mine = l.from === 'staff';
          const m = marks.get(l.id);
          return (
            <div key={l.id} style={{ justifySelf: mine ? 'end' : 'start', maxWidth: '75%' }} data-testid="chat-line">
              <small className="muted">{mine ? `${names.staff(l.staffId)}` : names.driver(driverId)}・{hm(l.at)}{l.kind === 'broadcast' && '・一斉連絡'}{l.urgent && <b className="err"> 至急</b>}</small>
              <div style={{ background: mine ? 'var(--ink, #111)' : 'var(--surface-2, #f1f1f1)', color: mine ? '#fff' : 'inherit', borderRadius: 12, padding: '8px 12px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {l.text}{l.deliveryNo && <> <Link to={`/deliveries?q=${l.deliveryNo}`} style={{ color: 'inherit', textDecoration: 'underline' }}>{l.deliveryNo}</Link></>}
              </div>
              {mine && <small className="muted">{m?.readAt ? `既読 ${hm(new Date(m.readAt))}` : m ? '未読' : sending.has(l.id) ? '送信中…' : '未読'}</small>}
              {!mine && m?.readBy && <small className="muted">{names.staff(m.readBy)}が読みました</small>}
            </div>
          );
        })}
        <div ref={bottom} />
      </div>
      <form onSubmit={send} style={{ padding: 12, borderTop: '1px solid var(--line)', display: 'grid', gap: 8 }}>
        <div role="group" aria-label="定型文" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {phrases.map((p) => <button key={p} type="button" className="btn sm" onClick={() => sendPhrase(p)}>{p}</button>)}
          <button type="button" className="btn sm" style={{ borderStyle: 'dashed' }} onClick={() => setEditing(true)}>定型文を直す</button>
        </div>
        <textarea aria-label="メッセージ" value={text} maxLength={RULES.chatTextMax} rows={2} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); (e.currentTarget.form as HTMLFormElement).requestSubmit(); } }}
          placeholder="メッセージ（Ctrl + Enter で送る）" style={{ resize: 'vertical' }} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}><input type="checkbox" style={{ width: 'auto' }} checked={urgent} onChange={(e) => setUrgent(e.target.checked)} /> 至急</label>
          <input aria-label="添付する配送番号（任意）" placeholder="配送番号（任意）" value={no} maxLength={6} onChange={(e) => setNo(e.target.value)} style={{ width: 150 }} />
          <small className="muted">{text.length} / {RULES.chatTextMax}</small>
          <span style={{ flex: 1 }} />
          {err && <span className="err" role="alert">{err}</span>}
          <button className="btn pri" disabled={!text.trim()}>送る</button>
        </div>
      </form>
      {editing && <PhraseDialog items={phrases} onClose={() => setEditing(false)} onSave={(items) => saveOfficePhrases(me.officeId, items)} />}
    </section>
  );
}

/** 一斉連絡（仕様書「事務所とのチャット」）：勤務中の人だけ、または全員へ。ドライバーからは返信だけ */
function BroadcastDialog({ me, onClose }: { me: { id: string; officeId: string }; onClose: () => void }) {
  const toast = useToast();
  const [rows, setRows] = useState<DriverStatusRow[] | null>(null);
  const [scope, setScope] = useState<'working' | 'all'>('working');
  const [text, setText] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get<{ drivers: DriverStatusRow[] }>('/v1/office/drivers/status').then((r) => setRows(r.drivers)).catch(() => setErr('ドライバーを読み込めませんでした')); }, []);
  const targets = (rows ?? []).filter((r) => scope === 'all' || r.shiftId).map((r) => r.driverId);
  const send = async () => {
    setErr('');
    const body = text.trim();
    if (!body) { setErr('文を入れてください'); return; }
    if (!targets.length) { setErr('宛先がいません'); return; }
    setBusy(true);
    try { const r = await broadcast(me, targets, body, urgent); toast(`${r.sent} 人に一斉連絡を送りました${r.failed ? `（${r.failed} 人には送れませんでした）` : ''}`); onClose(); }
    catch (e) { setErr(e instanceof HttpError ? e.message : '送れませんでした'); setBusy(false); }
  };
  return (
    <Dialog title="一斉連絡を送る" onClose={onClose}>
      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'flex', gap: 16 }}>
        <legend className="muted" style={{ fontSize: 13 }}>宛先</legend>
        <label><input type="radio" name="scope" style={{ width: 'auto' }} checked={scope === 'working'} onChange={() => setScope('working')} /> 勤務中の人だけ（{rows ? rows.filter((r) => r.shiftId).length : '…'} 人）</label>
        <label><input type="radio" name="scope" style={{ width: 'auto' }} checked={scope === 'all'} onChange={() => setScope('all')} /> 全員（{rows ? rows.length : '…'} 人）</label>
      </fieldset>
      <div className="field"><label htmlFor="bc-text">文</label><textarea id="bc-text" rows={3} maxLength={RULES.chatTextMax} value={text} onChange={(e) => setText(e.target.value)} placeholder="例：大雪のため本日は 30 分早めに出発してください" /></div>
      <label style={{ fontSize: 13 }}><input type="checkbox" style={{ width: 'auto' }} checked={urgent} onChange={(e) => setUrgent(e.target.checked)} /> 至急</label>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>ドライバーごとのチャットに届きます。返事はそれぞれのチャットに来ます。</p>
      {err && <p className="err" role="alert">{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={onClose}>やめる</button><button className="btn pri" disabled={busy || !rows} onClick={() => void send()}>{targets.length} 人に送る</button></div>
    </Dialog>
  );
}

/** 事務所の定型文を足す・直す・消す・並べ替える（スタッフ全員で同じ一覧） */
function PhraseDialog({ items, onSave, onClose }: { items: string[]; onSave: (items: string[]) => Promise<void>; onClose: () => void }) {
  const toast = useToast();
  const [list, setList] = useState(items);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const move = (i: number, d: number) => { const n = [...list]; [n[i], n[i + d]] = [n[i + d]!, n[i]!]; setList(n); };
  const save = async () => {
    const r = cleanPhrases(list);
    if ('error' in r) { setErr(r.error); return; }
    setBusy(true);
    try { await onSave(r.items); toast('定型文を保存しました'); onClose(); }
    catch { setErr('保存できませんでした'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title="定型文を直す" onClose={onClose}>
      <small className="muted">押すとそのまま送る文です。事務所のスタッフ全員で同じ一覧を使います（{RULES.phrasesMax} 個まで・1 つ {RULES.phraseMax} 文字まで）</small>
      <div style={{ display: 'grid', gap: 6 }}>
        {list.map((t, i) => (
          <div key={i} style={{ display: 'flex', gap: 4 }}>
            <input aria-label={`定型文 ${i + 1}`} value={t} maxLength={RULES.phraseMax} onChange={(e) => setList(list.map((x, j) => (j === i ? e.target.value : x)))} />
            <button type="button" className="btn sm" aria-label={`定型文 ${i + 1} を上へ`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
            <button type="button" className="btn sm" aria-label={`定型文 ${i + 1} を下へ`} disabled={i === list.length - 1} onClick={() => move(i, 1)}>↓</button>
            <button type="button" className="btn sm" aria-label={`定型文 ${i + 1} を消す`} onClick={() => setList(list.filter((_, j) => j !== i))}>×</button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn sm" disabled={list.length >= RULES.phrasesMax} onClick={() => setList([...list, ''])}>＋ 定型文を足す</button>
        <button type="button" className="btn sm" onClick={() => setList([...OFFICE_MESSAGE_TEMPLATES])}>初めの一覧に戻す</button>
      </div>
      {err && <p className="err" role="alert">{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn" onClick={onClose}>やめる</button>
        <button className="btn pri" disabled={busy} onClick={() => void save()}>保存する</button>
      </div>
    </Dialog>
  );
}
